import { FC, forwardRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcError from 'src/assets/images/icError.svg';
import IcInfo from 'src/assets/images/icInfo.svg';
import IcSuccess from 'src/assets/images/icSuccess.svg';
import Gap from 'src/components/Gap';
import { dismissToastAtom } from 'src/stores/toast/atoms';
import { ToastType, WebClientToastProps } from 'src/types/Toast';
import { hexToRgb } from 'src/utils/common';
import { ToastRootStyle as RootStyleBase } from './style';
const TOAST_BOUNCE_OFFSET = '50px';
const getToastOriginTop = (visualTop: string | undefined, defaultOriginTop: string) => visualTop ? `calc(${visualTop} - ${TOAST_BOUNCE_OFFSET})` : defaultOriginTop;
const RootStyle = styled(RootStyleBase, {
  shouldForwardProp: prop => prop !== '$top' && prop !== '$mobileTop'
})<{
  $top?: string;
  $mobileTop?: string;
}>`
  --top: ${({
  $top
}) => getToastOriginTop($top, '30px')};
  --gap: 16px;

  width: max-content;
  min-width: 320px;
  max-width: 640px;

  background-color: ${({
  theme
}) => theme.color.surface5};
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.5);

  ${({
  theme
}) => theme.screenSize.mobile} {
    --top: ${({
  $top,
  $mobileTop
}) => getToastOriginTop($mobileTop ?? $top, '-34px')};
    --gap: 8px;
    width: calc(var(--100vw) - 32px);
    min-width: auto;
    max-width: auto;
    box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.25);
  }
`;
const Inner = styled.div<{
  hasTitle?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${({
  theme
}) => theme.isRtl ? '16px 16px 16px 24px' : '16px 24px 16px 16px'};
  display: flex;
  flex-direction: row;
  align-items: ${({
  hasTitle
}) => hasTitle ? 'flex-start' : 'center'};
  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 12px 16px;
  }
`;
const IconBack = styled.div`
  border-radius: 43px;
  width: 32px;
  height: 32px;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  ${({
  theme
}) => theme.screenSize.tablet} {
    width: 20px;
    height: 20px;
    padding: 2px;
  }
  background-color: rgba(${({
  theme
}) => hexToRgb(theme.color.contentDeepFixed)}, 0.1);
  position: relative;
  width: 32px;
  height: 32px;
  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 20px;
    height: 20px;
    padding: 2px;
  }
`;
const Blur = styled.div<{
  type: ToastType;
}>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 212px;
  height: 212px;
  ${({
  type,
  theme
}) => {
  const color = hexToRgb({
    [ToastType.INFO]: theme.color.accentC1,
    [ToastType.WARNING]: theme.color.accentC1,
    [ToastType.SUCCESS]: theme.color.accentH1,
    [ToastType.ERROR]: theme.color.accentA1
  }[type]);
  return `
      background: radial-gradient(50% 50.00% at 50% 50.00%, rgba(${color}, 0.12) 0%, rgba(${color}, 0.00) 100%);
    `;
}}
`;
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const lineClamp = `
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const wordBreak = `
  word-break: break-word;
`;
const Title = styled.p<{
  ignoreLineClamp?: boolean;
}>`
  color: ${({
  theme
}) => theme.color.contentBrightFixed};
  font-weight: 700;
  line-height: 24px;
  font-size: 18px;
  ${wordBreak}
  ${({
  ignoreLineClamp
}) => !ignoreLineClamp && lineClamp}

  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 13px;
    line-height: 16px;
  }
`;
const Description = styled.p<{
  ignoreLineClamp?: boolean;
}>`
  color: ${({
  theme
}) => theme.color.content10};
  font-weight: 400;
  line-height: 16px;
  font-size: 13px;
  margin-top: 4px;
  ${wordBreak}
  ${({
  ignoreLineClamp
}) => !ignoreLineClamp && lineClamp}
`;
const Message = styled.p<{
  ignoreLineClamp?: boolean;
}>`
  color: ${({
  theme
}) => theme.color.contentBrightFixed};
  font-weight: 700;
  line-height: 24px;
  font-size: 18px;
  flex: 1;
  ${wordBreak}
  ${({
  ignoreLineClamp
}) => !ignoreLineClamp && lineClamp}

  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 13px;
    line-height: 16px;
  }
`;
const ActionButton = styled.button`
  flex-shrink: 0;
  align-self: center;
  padding: 6px 16px;
  border-radius: 100px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({
  theme
}) => theme.color.accentH1};
  font-weight: 700;
  font-size: 15px;
  line-height: 20px;
  white-space: nowrap;
`;
const TOAST_ICONS: { [key in ToastType]: FC<React.SVGProps<SVGSVGElement>> } = {
  [ToastType.INFO]: IcInfo,
  [ToastType.WARNING]: IcInfo,
  [ToastType.SUCCESS]: IcSuccess,
  [ToastType.ERROR]: IcError
};
interface Props {
  toast: WebClientToastProps;
  index: number;
}
const WebClientToast = forwardRef<HTMLDivElement, Props>(({
  toast,
  index
}, ref) => {
  const {
    uuid,
    message,
    title,
    type = ToastType.INFO,
    duration,
    Icon: CustomIcon,
    buttonText,
    onClickButton,
    ignoreLineClamp,
    top,
    mobileTop
  } = toast;
  const Icon = CustomIcon ?? TOAST_ICONS[type];
  const dismiss = useSetAtom(dismissToastAtom);
  const handleClickButton = useCallback(() => {
    dismiss(uuid);
    onClickButton?.();
  }, [dismiss, uuid, onClickButton]);
  const {
    t
  } = useTranslation();
  return <RootStyle key={uuid} ref={ref} data-testid='toast' data-toastType={type} durationMs={duration} index={index} $top={top} $mobileTop={mobileTop}>
      <Inner hasTitle={!!title}>
        <IconBack>
          <Blur type={type} />
          <Icon width={'100%'} height='100%' data-testid={`toast-icon-${type}`} />
        </IconBack>
        <Gap width={8} />
        {title ? <TextContainer>
            <Title ignoreLineClamp={ignoreLineClamp}>{t(title)}</Title>
            <Description ignoreLineClamp={ignoreLineClamp}>{t(message)}</Description>
          </TextContainer> : <Message ignoreLineClamp={ignoreLineClamp}>{t(message)}</Message>}
        {buttonText && <>
            <Gap width={8} />
            <ActionButton onClick={handleClickButton}>{t(buttonText)}</ActionButton>
          </>}
      </Inner>
    </RootStyle>;
});
WebClientToast.displayName = 'WebClientToast';
export default WebClientToast;